import {useState} from 'react';
import {addMonths, format} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {useNavigate} from 'react-router-dom';
import {logEventBooking} from 'main/analytics/analytics';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	ExamBookingEnum,
	FeaturesEnum,
	MyAppointmentsEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {resolveBookingError} from 'presentation/modules/booking/shared/utils/resolveBookingError';
import {useMediaQueries} from 'presentation/pages';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useGetExamsBookedMutation} from '../../mutations/useGetExamsBookedMutation';
import {usePostExamBookingMutation} from '../../mutations/usePostExamBookingMutation';
import {usePostExamRebookingMutation} from '../../mutations/usePostExamRebookingMutation';
import {HandleConfirmationParams, UseExamConfirmationBookingProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useExamConfirmationBooking({handleBack}: UseExamConfirmationBookingProps) {
	const navigate = useNavigate();
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [bookingState, setBookingState] = useState<OperationStateType | null>(null);
	const {mutateAsync: mutateBookingExam} = usePostExamBookingMutation();
	const {mutateAsync: mutateGetExamsBooked} = useGetExamsBookedMutation();
	const {mutateAsync: mutateRebookingExam} = usePostExamRebookingMutation();
	const {refetch} = useGetHomeQuery();
	const {
		beneficiary: {idadeC},
		companyPortal,
	} = useAccountDataStore(state => state.accountData);
	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);

	const {bookingNumber, isRebooking, protocolNumber} = bookingData;

	const isGenericError = bookingState === OperationStateTypesEnum.ERROR;
	const isDone = bookingState === OperationStateTypesEnum.DONE;
	const isSubmitting = bookingState === OperationStateTypesEnum.SUBMITTING;
	const isUnavailable = bookingState === OperationStateTypesEnum.UNAVAILABLE;

	const dataTagIdSuccess = isRebooking
		? AppointmentBookingEnum.REALIZOU_REMARCACAO_CONSULTA
		: AppointmentBookingEnum.REALIZOU_AGENDAMENTO_COM_SUCESSO;

	const alertModalpaperStyles = {
		style: {
			maxWidth: 420,
			margin: isMobile ? 16 : 'auto',
			flex: isMobile ? 1 : 'initial',
		},
	};

	const handleBookingAction = () => {
		if (isRebooking && bookingNumber) {
			return rebookExam(bookingNumber);
		}
		return bookExam();
	};

	const bookExam = async () => {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			await mutateBookingExam({
				cdTipoExame: Number(bookingData.specialtyCode),
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtAgendamento: bookingData.dateAt,
				horarioAgendamento: bookingData.hourAt,
				nuCelular: bookingData.phone,
				nuIdade: idadeC,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				email: bookingData.email,
				nmOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			const {exames} = await mutateGetExamsBooked({
				cdStatus: '0',
				dtFinal: format(addMonths(new Date(), 12), 'P', {locale: ptBR}),
				dtInicial: format(new Date(), 'P', {locale: ptBR}),
			});

			const bookedExam = exames.find(exam => exam.cdTipoExame === bookingData.specialtyCode);

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_EXAM_BOOKING_COMPLETE});

			if (!bookedExam) {
				navigate(MyAppointmentsEnum.MY_APPOINTMENTS);
				return;
			}

			handleConfirmOperation({
				id: bookedExam.nuExame.toString(),
				protocolNumber: bookedExam.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setBookingState(state);
		}
	};

	const rebookExam = async (bookingNumber: string) => {
		try {
			setBookingState(OperationStateTypesEnum.SUBMITTING);

			const {reagendamentoExame} = await mutateRebookingExam({
				cdTipoExame: Number(bookingData.specialtyCode),
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtAgendamento: bookingData.dateAt,
				horarioAgendamento: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				nuExame: bookingNumber,
				email: bookingData.email,
				nuCelular: bookingData.phone,
				nmOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_EXAM_BOOKING_COMPLETE});

			handleConfirmOperation({
				id: reagendamentoExame.nuExameRemarcado.toString(),
				protocolNumber: reagendamentoExame.protocoloAgendamento.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setBookingState(state);
		}
	};

	const handleConfirmOperation = async ({id, protocolNumber}: HandleConfirmationParams) => {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType: BookingTypeEnum.EXAM,
			bookingNumber: id,
		}));

		await refetch();

		if (isHideConfirmationBookingModalEnabled) {
			const path = ExamBookingEnum.EXAM_BOOKING_CONFIRMATION.replace(':id', id);

			navigate(path);
			setBookingState(null);
		} else {
			setBookingState(OperationStateTypesEnum.DONE);
		}
	};

	const handleCloseSuccessDialogState = () => {
		if (!bookingNumber) {
			setBookingState(null);
			return;
		}

		const path = ExamBookingEnum.EXAM_BOOKING_CONFIRMATION.replace(':id', bookingNumber);

		navigate(path);
		setBookingState(null);
	};

	const handleCloseAndRedirectToSchedule = () => {
		setBookingState(null);
		handleBack();
	};

	return {
		handleBookingAction,
		isSubmitting,
		isGenericError,
		isDone,
		isHideConfirmationBookingModalEnabled,
		dataTagIdSuccess,
		handleCloseSuccessDialogState,
		isUnavailable,
		handleCloseAndRedirectToSchedule,
		isMobile,
		setBookingState,
		protocolNumber,
		alertModalpaperStyles,
	};
}
